import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, ExternalLink } from 'lucide-react';

export default function AchievementsSection({ achievementSection }) {
  if (!achievementSection.display || !achievementSection.achievementsCards.length) return null;

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariant = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { type: 'spring', stiffness: 100 },
    },
  };

  return (
    <section id="achievements" className="py-20 px-4 bg-gray-50 dark:bg-slate-900">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            {achievementSection.title}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {achievementSection.subtitle}
          </p>
        </motion.div>

        {/* Achievement Cards */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-8"
        >
          {achievementSection.achievementsCards.map((cert, i) => (
            <motion.div
              key={i}
              variants={itemVariant}
              className="group"
            >
              <div className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden border border-gray-200 dark:border-slate-700 hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                {/* Certificate Image */}
                {cert.image && (
                  <div className="relative h-40 bg-gradient-to-br from-primary-500 to-secondary-500 overflow-hidden flex items-center justify-center">
                    <motion.img
                      whileHover={{ scale: 1.1 }}
                      src={cert.image}
                      alt={cert.title}
                      className="w-24 h-24 object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}

                {/* Content */}
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex items-start gap-2 mb-3">
                    <Trophy className="text-yellow-500 flex-shrink-0 mt-1" size={24} />
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      {cert.title}
                    </h3>
                  </div>

                  <p className="text-gray-600 dark:text-gray-400 mb-6 flex-grow">
                    {cert.subtitle}
                  </p>

                  {/* Links */}
                  {cert.footerLink && cert.footerLink.length > 0 && (
                    <div className="flex gap-3 flex-wrap">
                      {cert.footerLink.map((link, idx) => (
                        <motion.a
                          key={idx}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold transition-colors"
                        >
                          {link.name}
                          <ExternalLink size={16} />
                        </motion.a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
